import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PLAN_NAMES } from "../components/plan/PlanUI";
import { Shield, Loader2, Check, CreditCard, Users, TrendingUp, Star, MessageSquareQuote } from "lucide-react";

export default function Admin() {
  const { user, refresh } = useAuth();
  const [tab, setTab] = useState("alunos"); // alunos | assinaturas
  const [users, setUsers] = useState(null);
  const [err, setErr] = useState(null);
  const [savingEmail, setSavingEmail] = useState(null);
  const [savedEmail, setSavedEmail] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    if (user?.is_admin) load();
  }, [user, load]);

  if (user && !user.is_admin) return <Navigate to="/" replace />;

  if (tab === "assinaturas") {
    return (
      <AdminShell tab={tab} setTab={setTab}>
        <Assinaturas />
      </AdminShell>
    );
  }

  if (tab === "economia") {
    return (
      <AdminShell tab={tab} setTab={setTab}>
        <Economia />
      </AdminShell>
    );
  }

  if (tab === "avaliacoes") {
    return (
      <AdminShell tab={tab} setTab={setTab}>
        <Avaliacoes />
      </AdminShell>
    );
  }

  const setPlan = async (email, plano, dias) => {
    setSavingEmail(email); setErr(null); setSavedEmail(null);
    try {
      await api.post("/admin/set-plan", { email, plano, dias });
      await load();
      // Se mudou o próprio plano, atualiza o contexto (badge do header etc.)
      if (email === user.email) refresh();
      setSavedEmail(email);
      setTimeout(() => setSavedEmail(null), 2000);
    } catch (e) {
      setErr(formatApiError(e));
    }
    setSavingEmail(null);
  };

  return (
    <AdminShell tab={tab} setTab={setTab}>
      <p className="text-xs text-muted-foreground">
        Liberação manual (ex.: Pix direto). Quem assina pelo Mercado Pago é liberado sozinho pelo webhook.
      </p>

      {err && <p className="text-sm text-red-400">{err}</p>}

      {!users ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-violet-400" />
        </div>
      ) : (
        <div className="space-y-2.5">
          {users.map((u) => (
            <AlunoRow
              key={u.id}
              aluno={u}
              busy={savingEmail === u.email}
              saved={savedEmail === u.email}
              onSetPlan={setPlan}
            />
          ))}
        </div>
      )}
    </AdminShell>
  );
}

function AdminShell({ tab, setTab, children }) {
  return (
    <div data-testid="admin-page" className="space-y-5 max-w-3xl mx-auto">
      <div className="flex items-center gap-2">
        <Shield className="h-5 w-5 text-violet-400" />
        <h1 className="text-xl font-bold tracking-tight">Admin</h1>
      </div>
      <div className="flex gap-2">
        <TabBtn active={tab === "alunos"} onClick={() => setTab("alunos")} icon={Users} label="Alunos e planos" testId="admin-tab-alunos" />
        <TabBtn active={tab === "assinaturas"} onClick={() => setTab("assinaturas")} icon={CreditCard} label="Assinaturas" testId="admin-tab-assinaturas" />
        <TabBtn active={tab === "economia"} onClick={() => setTab("economia")} icon={TrendingUp} label="Economia" testId="admin-tab-economia" />
        <TabBtn active={tab === "avaliacoes"} onClick={() => setTab("avaliacoes")} icon={Star} label="Avaliações" testId="admin-tab-avaliacoes" />
      </div>
      {children}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-medium transition ${
        active
          ? "border-violet-500/60 bg-violet-500/15 text-violet-300"
          : "border-border text-muted-foreground hover:text-foreground hover:bg-muted"
      }`}
    >
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}

// Aba "Assinaturas": só conferência — a liberação é 100% automática (webhook MP)
function Assinaturas() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/admin/payments").then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e)));
  }, []);

  if (err) return <p className="text-sm text-red-400">{err}</p>;
  if (!data) {
    return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-violet-400" /></div>;
  }

  const statusCls = {
    ativo: "text-emerald-300",
    aprovado: "text-emerald-300",
    pendente: "text-amber-300",
    cancelado: "text-red-300",
    vencido: "text-red-300",
  };

  return (
    <div className="space-y-6" data-testid="admin-assinaturas">
      <p className="text-xs text-muted-foreground">
        Quem pagou, plano, status e validade. A liberação é automática pelo webhook do Mercado Pago — aqui é só conferência.
      </p>

      <div>
        <h2 className="text-sm font-bold tracking-tight mb-2">Assinantes ({data.assinantes.length})</h2>
        {data.assinantes.length === 0 && <p className="text-xs text-muted-foreground">Nenhum assinante ainda.</p>}
        <div className="space-y-2">
          {data.assinantes.map((a) => (
            <div key={a.id} className="rounded-xl border border-border bg-card p-3.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              <span className="text-sm font-medium">{a.name || a.email}</span>
              <span className="text-muted-foreground">{a.email}</span>
              <span className="rounded-full border border-violet-500/40 bg-violet-500/10 text-violet-300 px-2 py-0.5">
                {PLAN_NAMES[a.plano] || a.plano}
              </span>
              <span className={statusCls[a.pagamento_status] || "text-muted-foreground"}>
                {a.pagamento_status || "—"}
              </span>
              {a.plano_expira_em && (
                <span className="text-muted-foreground">válido até {new Date(a.plano_expira_em).toLocaleDateString("pt-BR")}</span>
              )}
              {a.mp_preapproval_id && <span className="text-muted-foreground/60">MP: {a.mp_preapproval_id}</span>}
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-sm font-bold tracking-tight mb-2">Histórico de pagamentos ({data.pagamentos.length})</h2>
        {data.pagamentos.length === 0 && <p className="text-xs text-muted-foreground">Nenhum pagamento registrado.</p>}
        <div className="space-y-1.5">
          {data.pagamentos.map((p) => (
            <div key={p.id} className="rounded-lg bg-secondary px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
              <span className="font-medium">{p.aluno}</span>
              <span className="text-muted-foreground">{PLAN_NAMES[p.plano] || p.plano}</span>
              <span>R$ {(p.valor ?? 0).toFixed(2).replace(".", ",")}</span>
              <span className={statusCls[p.status] || "text-muted-foreground"}>{p.status}</span>
              <span className="text-muted-foreground/70 ml-auto">
                {p.created_at ? new Date(p.created_at).toLocaleString("pt-BR") : ""}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Aba "Economia": receita × custo de IA por plano — só leitura, pra ver se o plano se paga
function Economia() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/admin/economia").then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e)));
  }, []);

  if (err) return <p className="text-sm text-red-400">{err}</p>;
  if (!data) {
    return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-violet-400" /></div>;
  }

  const usd = (v) => `US$ ${(v ?? 0).toFixed(2)}`;
  const brl = (v) => `R$ ${(v ?? 0).toFixed(2).replace(".", ",")}`;
  const receitaTotal = data.planos.reduce((s, p) => s + (p.receita_brl || 0), 0);
  const custoTotal = data.planos.reduce((s, p) => s + (p.custo_ia_usd || 0), 0);

  return (
    <div className="space-y-6" data-testid="admin-economia">
      <p className="text-xs text-muted-foreground">
        Mês <strong className="text-foreground">{data.mes}</strong> · receita estimada{" "}
        <strong className="text-emerald-300">{brl(receitaTotal)}</strong> · custo de IA estimado{" "}
        <strong className="text-amber-300">{usd(custoTotal)}</strong>
        {!data.modelo_pago_configurado && (
          <span className="block mt-1">Modelo pago do Pro ainda não configurado — o Pro está rodando no Groq 70b (custo real R$0).</span>
        )}
      </p>

      <div className="grid gap-3 sm:grid-cols-3">
        {data.planos.map((p) => (
          <div key={p.plano} data-testid={`economia-${p.plano}`} className="rounded-xl border border-border bg-card p-4 space-y-2">
            <p className="font-bold tracking-tight text-sm">{p.nome}</p>
            <div className="text-xs text-muted-foreground space-y-1">
              <p><strong className="text-foreground">{p.usuarios}</strong> aluno{p.usuarios === 1 ? "" : "s"}</p>
              <p>Receita/mês: <strong className="text-emerald-300">{brl(p.receita_brl)}</strong></p>
              <p>Custo IA/mês: <strong className="text-amber-300">{usd(p.custo_ia_usd)}</strong></p>
              <p>{p.chamadas_ia} chamada{p.chamadas_ia === 1 ? "" : "s"} de IA</p>
            </div>
          </div>
        ))}
      </div>

      {data.modelos.length > 0 && (
        <div>
          <h2 className="text-sm font-bold tracking-tight mb-2">Custo por modelo (mês atual)</h2>
          <div className="space-y-1.5">
            {data.modelos.map((m, i) => (
              <div key={i} className="rounded-lg bg-secondary px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                <span className="font-medium">{m.modelo}</span>
                <span className="text-muted-foreground">plano {m.plano}</span>
                <span className="text-muted-foreground">{m.chamadas} chamadas</span>
                <span className="text-muted-foreground">{((m.input_tokens + m.output_tokens) / 1000).toFixed(1)}k tokens</span>
                <span className="ml-auto text-amber-300">{usd(m.custo_usd)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground">{data.nota}</p>
    </div>
  );
}

// Aba "Avaliações": notas e comentários das conversas. Só quem tem 4+ estrelas,
// comentário e consentimento LGPD pode ser publicado como depoimento — e mesmo
// assim só quando o Antonio aprovar aqui.
function Avaliacoes() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/feedback").then((r) => setData(r.data)).catch((e) => setErr(formatApiError(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (fb) => {
    setBusyId(fb.id); setErr(null);
    try {
      await api.post(`/admin/feedback/${fb.id}/depoimento`, { aprovado: !fb.aprovado_depoimento });
      load();
    } catch (e) {
      setErr(formatApiError(e));
    }
    setBusyId(null);
  };

  if (err && !data) return <p className="text-sm text-red-400">{err}</p>;
  if (!data) {
    return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-violet-400" /></div>;
  }

  const media = data.avaliacoes.length
    ? (data.avaliacoes.reduce((s, a) => s + (a.stars || 0), 0) / data.avaliacoes.length).toFixed(1)
    : null;

  return (
    <div className="space-y-4" data-testid="admin-avaliacoes">
      <p className="text-xs text-muted-foreground">
        {data.total} avaliaç{data.total === 1 ? "ão" : "ões"}{media && <> · média <strong className="text-amber-300">{media}★</strong></>}.
        Só entram como candidatos a depoimento as com <strong className="text-foreground">4+ estrelas, comentário e consentimento</strong> — e nada é publicado sem você aprovar.
      </p>
      {err && <p className="text-sm text-red-400">{err}</p>}
      {data.avaliacoes.length === 0 && <p className="text-xs text-muted-foreground">Nenhuma avaliação ainda.</p>}
      <div className="space-y-2.5">
        {data.avaliacoes.map((a) => (
          <div key={a.id} className="rounded-xl border border-border bg-card p-4 space-y-2.5">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              <span className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star key={s} className={`h-3.5 w-3.5 ${s <= a.stars ? "text-amber-400 fill-amber-400" : "text-border"}`} />
                ))}
              </span>
              <span className="text-sm font-medium">{a.aluno_nome}</span>
              <span className="text-muted-foreground">{a.aluno_email}</span>
              {a.consent_publico && (
                <span className="rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 px-2 py-0.5 text-[10px]">
                  consentiu uso público
                </span>
              )}
              {a.aprovado_depoimento && (
                <span className="rounded-full border border-violet-500/40 bg-violet-500/10 text-violet-300 px-2 py-0.5 text-[10px] inline-flex items-center gap-1">
                  <MessageSquareQuote className="h-3 w-3" /> no site
                </span>
              )}
              <span className="ml-auto text-muted-foreground/70">
                {a.created_at ? new Date(a.created_at).toLocaleString("pt-BR") : ""}
              </span>
            </div>
            {a.comment ? (
              <p className="text-sm text-foreground/90">“{a.comment}”</p>
            ) : (
              <p className="text-xs italic text-muted-foreground">sem comentário</p>
            )}
            {a.candidato && (
              <button
                data-testid={`admin-depoimento-toggle-${a.id}`}
                onClick={() => toggle(a)}
                disabled={busyId === a.id}
                className={`rounded-full px-4 py-2 text-xs font-medium transition inline-flex items-center gap-1.5 disabled:opacity-50 ${
                  a.aprovado_depoimento
                    ? "border border-border text-muted-foreground hover:text-foreground hover:bg-muted"
                    : "bg-violet-600 hover:bg-violet-500 text-white"
                }`}
              >
                {busyId === a.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <MessageSquareQuote className="h-3 w-3" />}
                {a.aprovado_depoimento ? "Tirar do site" : "Publicar como depoimento"}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function AlunoRow({ aluno, busy, saved, onSetPlan }) {
  const [plano, setPlano] = useState(aluno.plano || "gratis");
  const [dias, setDias] = useState(31);
  const expira = aluno.plano_expira_em ? new Date(aluno.plano_expira_em).toLocaleDateString("pt-BR") : null;
  const um = aluno.uso_mensal;

  return (
    <div data-testid={`admin-user-${aluno.email}`} className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <p className="text-sm font-medium">{aluno.name || aluno.email}</p>
        <p className="text-xs text-muted-foreground">{aluno.email}</p>
        {aluno.is_admin && <span className="text-[10px] rounded-full bg-violet-500/15 text-violet-300 px-2 py-0.5">admin</span>}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>
          Plano: <strong className="text-foreground">{PLAN_NAMES[aluno.plano] || aluno.plano || "Grátis"}</strong>
          {expira && <> · expira {expira}</>}
        </span>
        {um && (
          <span>· Uso em {um.mes}: {um.chat || 0} msgs, {um.quiz || 0} quizzes</span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={plano}
          onChange={(e) => setPlano(e.target.value)}
          className="rounded-lg bg-secondary px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-violet-500/50"
        >
          <option value="gratis">Grátis</option>
          <option value="medio">Médio</option>
          <option value="pro">Pro</option>
        </select>
        {plano !== "gratis" && (
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            por
            <input
              type="number" min={1} max={366} value={dias}
              onChange={(e) => setDias(parseInt(e.target.value, 10) || 31)}
              className="w-16 rounded-lg bg-secondary px-2 py-2 text-xs outline-none focus:ring-2 focus:ring-violet-500/50"
            />
            dias
          </label>
        )}
        <button
          onClick={() => onSetPlan(aluno.email, plano, dias)}
          disabled={busy}
          className="rounded-full bg-violet-600 hover:bg-violet-500 text-white px-4 py-2 text-xs font-medium disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : saved ? <Check className="h-3 w-3" /> : null}
          {saved ? "Aplicado" : "Aplicar"}
        </button>
      </div>
    </div>
  );
}
